import type { QrNutzlast } from './qrNutzlast';

/**
 * Was ein Auswahlcode an **dieser** Prüfung wirklich trifft — und ob das zum Starten reicht.
 *
 * Herausgelöst aus `features/sheets/SetupSheet.tsx`, damit der Entscheid „gleich starten oder
 * stehen bleiben" ohne React geprüft werden kann (`vitest.config.ts` läuft mit
 * `environment: 'node'`, das Projekt hat keine Infrastruktur zum Rendern von Komponenten) —
 * dieselbe Begründung wie bei `features/annotate/annotateSave.ts`.
 *
 * ⚠️ **Ein Code kann auf Dinge zeigen, die dieses iPad nicht hat.** Er wird woanders erzeugt
 * (vom Classic-Server oder mit `tools/qr-auswahl.mjs`), und eine Station, die hier nie geladen
 * wurde, ist keine Wahl — sie wäre eine leere Zeile im Kopf des Bogens. Was im Code steht und
 * hier fehlt, macht `fehlt` wahr; dann bleibt das Blatt offen und der Prüfer sieht, was nicht
 * ging.
 */

/** Die Listen, gegen die geprüft wird — nur die Kennungen zählen. */
export interface VorhandeneWahl {
  stations: { id: string }[];
  examiners: { id: string }[];
  actors: { id: string }[];
}

export interface QrTreffer {
  station: string | null;
  examiner: string | null;
  actor: string | null;
  /** Etwas stand im Code und ist hier nicht zu finden. */
  fehlt: boolean;
  /**
   * Reicht die Wahl zum Starten?
   *
   * Dieselbe Schwelle wie der Bestätigen-Knopf des Blattes (`SetupSheet::vollständig` und
   * `store::confirmSetup`): Prüfung, **Station** und **Prüfer**. Der Schauspieler gehört nicht
   * dazu — nicht jede Station führt einen. Steht er aber im Code, muss er auch hier sein:
   * sonst startete die App mit einer Zuordnung, die der Code anders meinte.
   */
  starten: boolean;
}

export function trefferAusCode(
  gelesen: Extract<QrNutzlast, { art: 'auswahl' }>,
  vorhanden: VorhandeneWahl,
): QrTreffer {
  const treffer = (liste: { id: string }[], gesucht: string | null) =>
    gesucht ? (liste.find((x) => x.id === gesucht)?.id ?? null) : null;

  const station = treffer(vorhanden.stations, gelesen.station);
  const examiner = treffer(vorhanden.examiners, gelesen.examiner);
  const actor = treffer(vorhanden.actors, gelesen.actor);

  const fehlt =
    (!!gelesen.station && !station) ||
    (!!gelesen.examiner && !examiner) ||
    (!!gelesen.actor && !actor);

  return { station, examiner, actor, fehlt, starten: !fehlt && !!station && !!examiner };
}
